/**
 * JoinTeamPage - Team Invite Acceptance Flow
 * 
 * Handles the flow when a user clicks an invite link:
 * 1. Validate the invite token
 * 2. Show invite details
 * 3. If not logged in, prompt to sign up/login
 * 4. Accept invite and redirect to team
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  ArrowRight,
  Clock,
  Mail,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { validateInvite, acceptInvite } from '../../services/teamsService';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JoinTeamPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  
  // Signup form state (for new users)
  const [signupMode, setSignupMode] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);

  useEffect(() => {
    const checkInvite = async () => {
      try {
        const data = await validateInvite(token);
        setInviteData(data);
        
        if (data.invitee_email) {
          setEmail(data.invitee_email);
        }
        
        // If already a member, just show success
        if (data.already_member) {
          setAccepted(true);
        }
      } catch (err) {
        setError('Failed to load invite details');
      } finally {
        setLoading(false);
      }
    };
    
    checkInvite();
  }, [token]);

  const handleAcceptInvite = async () => {
    setAccepting(true);
    setError(null);
    
    try {
      const result = await acceptInvite(token);
      setAccepted(true);
      
      // Redirect to team after a brief delay
      setTimeout(() => {
        navigate(`/dashboard/teams/${result.team_id}`);
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupLoading(true);
    setSignupError('');
    
    if (!name.trim()) {
      setSignupError('Please enter your name');
      setSignupLoading(false);
      return;
    }
    
    try {
      // Sign up with team_invite source for 28-day trial
      const response = await fetch(`${API_URL}/api/preview-campaign/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          source: 'team_invite',
          coach_opt_in: true
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }
      
      setSignupSuccess(true);
    } catch (err) {
      setSignupError(err.message);
    } finally {
      setSignupLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    );
  }

  // Invalid invite
  if (!inviteData?.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur border-white/20">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invalid Invite</h2>
            <p className="text-zinc-400 mb-6">
              {inviteData?.message || 'This invite link is invalid or has expired.'}
            </p>
            <Link to="/teams">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Visit Teams Page
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state - accepted
  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="max-w-md w-full bg-white/10 backdrop-blur border-green-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                Welcome to {inviteData.team_name}!
              </h2>
              <p className="text-zinc-400 mb-4">
                You're now a member of the team. Redirecting...
              </p>
              <Loader2 className="w-5 h-5 text-violet-400 animate-spin mx-auto" />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Signup success - check email
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur border-green-500/30">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Check Your Email</h2>
            <p className="text-zinc-400 mb-4">
              We've sent a login link to <strong className="text-white">{email}</strong>.
              Click the link in the email to complete signing up and join the team.
            </p>
            <p className="text-zinc-500 text-sm">
              You'll get a <strong className="text-violet-400">28-day free trial</strong> as a team member.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main invite view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card className="bg-white/10 backdrop-blur border-white/20">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Users className="w-7 h-7 text-violet-400" />
            </div>
            <CardTitle className="text-2xl text-white">
              You're Invited!
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Join <strong className="text-white">{inviteData.team_name}</strong>
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Team Info */}
            {inviteData.team_description && (
              <p className="text-zinc-400 text-sm text-center">
                {inviteData.team_description}
              </p>
            )}
            
            {/* Inviter Info */}
            <div className="bg-white/5 rounded-lg p-4 text-center">
              <p className="text-zinc-400 text-sm">Invited by</p>
              <p className="text-white font-medium">{inviteData.invited_by_name}</p>
              <p className="text-zinc-500 text-xs">{inviteData.invited_by_email}</p>
            </div>
            
            {/* Personal Message */}
            {inviteData.message && (
              <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4">
                <p className="text-zinc-300 italic">"{inviteData.message}"</p>
              </div>
            )}
            
            {/* Expiry Notice */}
            <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
              <Clock className="w-4 h-4" />
              <span>
                Expires {new Date(inviteData.expires_at).toLocaleDateString()}
              </span>
            </div>
            
            {/* Action Area */}
            {isAuthenticated ? (
              // User is logged in - show accept button
              <div className="space-y-3">
                {user?.email?.toLowerCase() !== inviteData.invitee_email?.toLowerCase() && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-amber-300 text-sm">
                      This invite was sent to <strong>{inviteData.invitee_email}</strong>. 
                      You're logged in as <strong>{user?.email}</strong>.
                    </p>
                  </div>
                )}
                
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-300 text-sm">
                    {error}
                  </div>
                )}
                
                <Button
                  onClick={handleAcceptInvite}
                  disabled={accepting || user?.email?.toLowerCase() !== inviteData.invitee_email?.toLowerCase()}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="accept-invite-btn"
                >
                  {accepting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Join Team
                </Button>
              </div>
            ) : inviteData.needs_signup ? (
              // User needs to sign up
              signupMode ? (
                <form onSubmit={handleSignup} className="space-y-3">
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-zinc-500"
                    data-testid="join-signup-name"
                  />
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    disabled
                    className="bg-white/5 border-white/10 text-zinc-400"
                  />
                  
                  {signupError && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-300 text-sm">
                      {signupError}
                    </div>
                  )}
                  
                  <Button
                    type="submit"
                    disabled={signupLoading}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="join-signup-btn"
                  >
                    {signupLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    Create Account & Join
                  </Button>
                  
                  <p className="text-zinc-500 text-xs text-center">
                    You'll get a <strong className="text-violet-400">28-day free trial</strong>
                  </p>
                </form>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={() => setSignupMode(true)}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="start-signup-btn"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Sign Up to Join
                  </Button>
                  <p className="text-zinc-500 text-xs text-center">
                    Free 28-day trial · No credit card required
                  </p>
                </div>
              )
            ) : (
              // User has account but not logged in
              <div className="space-y-3">
                <Link to={`/login?redirect=/teams/join/${token}`}>
                  <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Log In to Join
                  </Button>
                </Link>
                <p className="text-zinc-500 text-xs text-center">
                  You already have an account with <strong>{inviteData.invitee_email}</strong>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default JoinTeamPage;
